import { useClipboard } from '@/hooks';
import TrackingService from '@/services/tracking';
import { theme } from '@/styles';
import { ShopDetailViewInfo } from '@/types';
import ContentsButton from '@/ui/ContentsButton';
import Close20Icon from '@/ui/Icons/Close20Icon';
import Copy16Icon from '@/ui/Icons/Copy16Icon';
import QuestionIcon from '@/ui/Icons/QuestionIcon';
import Tooltip from '@/ui/Tooltip/Tooltip';
import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { BookmarkButton } from '@design-system/pc';
interface FloatingInfoProps {
  data: ShopDetailViewInfo;
  handleScrap: () => void;
  isScrap: boolean;
  handleConsult: (value: 'quick' | 'visit') => void;
}

const infoList = [
  { code: 'tell', name: '전화번호' },
  { code: 'address', name: '매장주소' },
  { code: 'items', name: '취급항목' },
  { code: 'operatingTime', name: '운영시간' },
  { code: 'floorInfo', name: '층별정보' },
];

const FloatingInfo = ({
  data,
  isScrap,
  handleScrap,
  handleConsult,
}: FloatingInfoProps) => {
  const { handleCopy } = useClipboard();
  const [copyState, setCopyState] = useState<boolean>(false);
  const { header, bottomBar } = data;

  return (
    <FloatingContainer {...TrackingService.tagGTM('shop_info')}>
      <StoreKindContainer>
        <StoreKind>{header?.type.name}</StoreKind>
        {header.type.code !== 'S04' && (
          <Tooltip
            title={header?.tooltipTitle}
            text={header?.tooltipDescription}
            position="left"
            gtmName="shop_info_type_tooltip"
          >
            <IconBox>
              <QuestionIcon />
            </IconBox>
          </Tooltip>
        )}
      </StoreKindContainer>
      <StoreName>{header?.name}</StoreName>
      <ButtonContainer>
        <BookmarkIcon
          onClick={handleScrap}
          {...TrackingService.tagGTM('shop_info_bookmark_button')}
        >
          <BookmarkButton variant="whiteBG" size={'lg'} isChecked={isScrap} />
        </BookmarkIcon>
        <ContentsButton
          size="regular"
          emphasis="high"
          disabled={!bottomBar.reserveVisitYn}
          onClick={() => {
            handleConsult('visit');
          }}
          style={{
            width: '100%',
            cursor: !bottomBar.reserveVisitYn ? 'default' : 'pointer',
          }}
          {...TrackingService.tagGTM('shop_info_visit_consult_button')}
        >
          매장 상담 예약
        </ContentsButton>
      </ButtonContainer>
      <InfoContainer>
        {infoList.map((item, index) => {
          return (
            <Fragment key={`info_list_fragment_${index}`}>
              {item.code === 'tell' && header?.telNumber.telYn === 'Y' && (
                <InfoListItem className="InfoList">
                  <InfoTitle>{item.name}</InfoTitle>
                  <InfoContents>
                    <Content>{header?.telNumber.number}</Content>
                  </InfoContents>
                </InfoListItem>
              )}
              {item.code === 'address' && (
                <InfoListItem className="InfoList">
                  <InfoTitle>{item.name}</InfoTitle>
                  <InfoContents>
                    <Content>
                      {header?.address.basic} {header?.address.detail}
                    </Content>
                  </InfoContents>
                  <IconBox
                    onClick={() => {
                      handleCopy(
                        `${header?.address.basic} ${header?.address.detail}`,
                      );
                      setCopyState(true);
                    }}
                    {...TrackingService.tagGTM('shop_info_address_copy_button')}
                  >
                    <Copy16Icon />
                    {copyState && (
                      <CopyTooltip>
                        <CopyTooltipText>
                          {`매장 주소가 클립보드에\n복사되었습니다.`}
                        </CopyTooltipText>
                        <CopyTooltipIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setCopyState(false);
                          }}
                          {...TrackingService.tagGTM(
                            'shop_info_address_copy_tooltip_close_button',
                          )}
                        >
                          <Close20Icon />
                        </CopyTooltipIcon>
                      </CopyTooltip>
                    )}
                  </IconBox>
                </InfoListItem>
              )}

              {item.code === 'items' && (
                <InfoListItem className="InfoList">
                  <InfoTitle>{item.name}</InfoTitle>
                  <InfoContents>
                    <Content>{header?.includeItem}</Content>
                  </InfoContents>
                </InfoListItem>
              )}

              {item.code === 'operatingTime' && (
                <InfoListItem className="InfoList">
                  <InfoTitle>{item.name}</InfoTitle>
                  <InfoContents>
                    <Content>
                      {header?.operateTime.weekday.day}{' '}
                      {header?.operateTime.weekday.time}
                    </Content>
                    <Content>
                      {' '}
                      {header?.operateTime.weekend.day}{' '}
                      {header?.operateTime.weekend.time}
                    </Content>
                    <Content>
                      {' '}
                      {header?.operateTime.closed.day}{' '}
                      {header?.operateTime.closed.time}
                    </Content>
                  </InfoContents>
                </InfoListItem>
              )}

              {item.code === 'floorInfo' && header?.floorGuides.length !== 0 && (
                <InfoListItem className="InfoList">
                  <InfoTitle>{item.name}</InfoTitle>
                  <InfoContents>
                    {header?.floorGuides.map((_, idx) => (
                      <Content key={`${idx}_floorInfo`}>
                        {_.floor} {_.description}
                      </Content>
                    ))}
                    <Content>{header?.amenities}</Content>
                  </InfoContents>
                </InfoListItem>
              )}
            </Fragment>
          );
        })}
      </InfoContainer>
    </FloatingContainer>
  );
};

export default FloatingInfo;

const FloatingContainer = styled.div`
  min-width: 352px;
  width: 352px;
  margin-top: 40px;
  position: sticky;
  top: 184px;
  height: fit-content;
  bottom: 0px;
`;
const StoreKindContainer = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

const StoreKind = styled.div`
  color: ${theme.colors.neutral_colors.gray[700]};
  ${theme.typography.body2};
`;

const IconBox = styled.div`
  width: 16px;
  height: 16px;
  position: relative;
  cursor: pointer;
`;

const StoreName = styled.div`
  display: block;
  color: ${theme.colors.accent_colors.primary.default};
  ${theme.typography.subtitle2};
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  width: 352px;
  padding-right: 10px;
  gap: 8px;
  margin-bottom: 36px;
`;

const BookmarkIcon = styled.div`
  min-width: 48px;
  min-height: 48px;
  cursor: pointer;
`;

const InfoContainer = styled.div`
  width: 100%;
  padding: 24px 28px 24px 36px;
  max-height: 537px;
  overflow: auto;
  border-radius: 2px;
  background-color: ${theme.colors.accent_colors.primary.light};
`;

const InfoTitle = styled.div`
  display: block;
  min-width: 60px;
  color: ${theme.colors.neutral_colors.gray[600]};
  ${theme.typography.body2};
`;

const InfoListItem = styled.div`
  display: flex;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.neutral_colors.gray[200]};
  gap: 10px;
  :first-child {
    padding-top: 0px;
  }
  :last-child {
    padding-bottom: 0px;
    border: none;
  }
`;

const InfoContents = styled.div`
  /* display: block; */
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  word-break: keep-all;
  text-overflow: ellipsis;
  color: ${theme.colors.accent_colors.primary.default};
  ${theme.typography.body2};
`;

// const ContentTwo = styled.div``;

const CopyTooltip = styled.div`
  position: absolute;
  display: flex;
  width: 232px;
  height: 72px;
  padding: 16px 12px 16px 16px;
  gap: 4px;
  right: 0px;
  justify-content: space-between;
  border-radius: 4px;
  border: 1px solid ${theme.colors.neutral_colors.gray[500]};
  background-color: ${theme.colors.neutral_colors.white};
`;

const CopyTooltipText = styled.div`
  color: ${theme.colors.accent_colors.primary.default};
  white-space: pre-wrap;
  ${theme.typography.body2};
`;

const CopyTooltipIcon = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;
